let instance: SoundEngine | null = null;

export class SoundEngine {
  private audioContext: AudioContext;
  private musicGain: GainNode;
  private sfxGain: GainNode;
  private isMuted: boolean = false;
  private musicBuffer: AudioBuffer | null = null;
  private currentMusicSource: AudioBufferSourceNode | null = null;
  private sfxBuffers: Map<string, AudioBuffer> = new Map();
  private musicBuffers: Map<string, AudioBuffer> = new Map();

  constructor() {
    this.audioContext = new AudioContext();
    this.musicGain = this.audioContext.createGain();
    this.sfxGain = this.audioContext.createGain();
    this.musicGain.connect(this.audioContext.destination);
    this.sfxGain.connect(this.audioContext.destination);
  }

  async loadMusic(url: string) {
    const buffer = await this.loadAudioBuffer(url);
    this.musicBuffer = buffer;
  }

  playMusic(loop = true) {
    if (!this.musicBuffer) return;
    this.stopMusic();
    const source = this.audioContext.createBufferSource();
    source.buffer = this.musicBuffer;
    source.loop = loop;
    source.connect(this.musicGain);
    source.start(0);
    this.currentMusicSource = source;
  }

  playMusicByKey(key: string, loop = true) {
    if (!this.musicBuffers.get(key)) return;
    this.stopMusic();
    const source = this.audioContext.createBufferSource();
    source.buffer = this.musicBuffers.get(key)!;
    this.musicBuffer = source.buffer;
    this.playMusic(loop);
  }

  isMusicPlaying(key: string) {
    if (!this.musicBuffers.get(key)) return false;
    return this.currentMusicSource?.buffer === this.musicBuffers.get(key);
  }

  stopMusic() {
    if (this.currentMusicSource) {
      this.currentMusicSource.stop();
      this.currentMusicSource.disconnect();
      this.currentMusicSource = null;
    }
  }

  async preloadSFX(key: string, url: string) {
    const buffer = await this.loadAudioBuffer(url);
    this.sfxBuffers.set(key, buffer);
  }

  async preloadMusic(key: string, url: string) {
    const buffer = await this.loadAudioBuffer(url);
    this.musicBuffers.set(key, buffer);
  }

  async resumeIfNeeded() {
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  playSFX(key: string) {
    if (!this.sfxBuffers.has(key)) return;
    const source = this.audioContext.createBufferSource();
    source.buffer = this.sfxBuffers.get(key)!;
    source.connect(this.sfxGain);
    source.start(0);
  }

  muteAll(muted: boolean) {
    this.isMuted = muted;
    this.musicGain.gain.value = muted ? 0 : 1;
    this.sfxGain.gain.value = muted ? 0 : 1;
  }

  setMusicVolume(volume: number) {
    const clamped = Math.max(0, Math.min(1, volume));
    this.musicGain.gain.value = clamped;
  }

  unloadMusic() {
    this.stopMusic();
    this.musicBuffer = null;
  }

  private async loadAudioBuffer(url: string): Promise<AudioBuffer> {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return await this.audioContext.decodeAudioData(arrayBuffer);
  }

  getState(): AudioContextState {
    return this.audioContext.state; // 'running', 'suspended', or 'closed'
  }

  isMusicLoaded(): boolean {
    return this.musicBuffer !== null;
  }
}

export const getSoundEngine = () => {
  if (typeof window === "undefined") return null;
  if (!instance) {
    instance = new SoundEngine();
  }
  return instance;
};
